// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import isNumeric from "@insite/client-framework/Common/isNumeric";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import waitFor from "@insite/client-framework/Common/Utilities/waitFor";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import Icon, { IconPresentationProps, IconWrapper } from "@insite/mobius/Icon";
import { TextFieldPresentationProps, TextFieldProps } from "@insite/mobius/TextField";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import TextField from "@steiner/MobiusOverrides/TextField";
import Minus from "@steiner/XDL/Icons/Minus";
import Plus from "@steiner/XDL/Icons/Plus";
import { color, maxQtyNumber, mergeWithCss } from "@steiner/XDL/Utilities";
import React, { FC, useEffect, useRef, useState } from "react";
import { css } from "styled-components";

interface OwnProps extends TextFieldProps {
    showActions?: boolean;
    formId?: string;
    editable?: boolean;
    extendedStyles?: QtyFieldStyle;
    showLabel?: boolean;
    customError?: string;
}

export interface QtyFieldStyle extends TextFieldPresentationProps {
    container?: InjectableCss;
    actionClickable?: ClickablePresentationProps;
    countIcon?: IconPresentationProps;
    labelText?: TypographyPresentationProps;
    errorMessage?: TypographyPresentationProps;
    wrapper?: InjectableCss;
}

const qtyFieldStyle: QtyFieldStyle = {
    wrapper: {
        css: css`
            display: flex;
            flex-direction: column;
        `,
    },
    container: {
        css: css`
            display: flex;
            align-items: center;
            background: ${color("common.ui15")};
            max-width: 104px;
            padding: 0 2px;
        `,
    },
    actionClickable: {
        css: css`
            height: 100%;
            padding: 0 6px;
            &:disabled {
                opacity: 0.33;
            }
            &:not(:disabled):hover > ${IconWrapper} {
                color: ${color("common.ui9")};
            }
        `,
    },
    countIcon: {
        size: 12,
        color: "primary",
    },
    cssOverrides: {
        formField: css`
            & input:not(.react-datetime-picker__inputGroup__input):not([name="datetime"]) {
                text-align: center;
                border: none;
                min-width: 52px;

                &:hover:not(:disabled) {
                    border: none;
                }
                &:focus:not(:disabled) {
                    border: none;
                }

                @media print {
                    &:disabled {
                        opacity: 1;
                    }
                }
            }
        `,
        inputSelect: css`
            padding: 0;
        `,
    },
    labelText: {
        forwardAs: "p",
        size: 12,
        lineHeight: "15px",
        css: css`
            margin-bottom: 4px;
        `,
    },
    errorMessage: {
        color: "danger",
        size: 14,
        css: css`
            max-width: 104px;
        `,
    },
};

type Props = OwnProps;

const getQtyValue = (value?: Props["value"]) => {
    return value && isNumeric(`${value || ""}`) ? +value : 0;
};

const QtyField: FC<Props> = ({
    showActions = true,
    editable = true,
    extendedStyles,
    label,
    showLabel = false,
    customError,
    ...props
}) => {
    const [errorMessage, setErrorMessage] = useState("");
    const [styles] = useState(() => mergeWithCss(qtyFieldStyle, extendedStyles));
    const isDisabled = props.disabled && !editable;

    /**
     * In CartLineQty, values come from hooks.
     * A ref is used to prevent displaying outdated values when calling onBlur within useAccessibleSubmit.
     */
    const eventRef = useRef<any | null>(null);
    const valueRef = useRef(props.value);
    valueRef.current = props.value;
    const onBlurRef = useRef(props.onBlur);
    onBlurRef.current = props.onBlur;

    const canDecrease = getQtyValue(props.value) - 1 >= 1;
    const canIncrease = getQtyValue(props.value) + 1 <= maxQtyNumber;

    const onDecreaseClickHandler = async (event: any) => {
        const currentValue = getQtyValue(props.value);
        if (!canDecrease || isDisabled) {
            return;
        }

        eventRef.current = {
            ...event,
            target: {
                value: currentValue === 0 ? 0 : currentValue - 1,
            },
            currentTarget: {
                value: currentValue === 0 ? 0 : currentValue - 1,
            },
        };
        props.onChange?.(eventRef.current);
        await waitFor(() => getQtyValue(valueRef.current) === currentValue - 1);
        onBlurRef.current?.(eventRef.current);
    };

    const onIncreaseClickHandler = async (event: any) => {
        const currentValue = getQtyValue(props.value);
        if (!canIncrease || isDisabled) {
            return;
        }
        eventRef.current = {
            ...event,
            target: {
                value: currentValue + 1,
            },
            currentTarget: {
                value: currentValue + 1,
            },
        };
        props.onChange?.(eventRef.current);
        await waitFor(() => getQtyValue(valueRef.current) === currentValue + 1);
        onBlurRef.current?.(eventRef.current);
    };

    const errorHandler = (error: string) => {
        setErrorMessage(error);
    };

    useEffect(() => {
        setErrorMessage(customError || "");
    }, [customError]);

    return (
        <StyledWrapper {...styles.wrapper} className="qtyFieldWrapper">
            {showLabel && <Typography {...styles.labelText}>{label}</Typography>}
            <StyledWrapper {...styles.container}>
                {showActions && (
                    <Clickable
                        {...styles.actionClickable}
                        type="button"
                        onClick={onDecreaseClickHandler}
                        aria-label="Decrease quantity"
                        disabled={isDisabled || !canDecrease}
                    >
                        <Icon {...styles.countIcon} src={Minus} />
                    </Clickable>
                )}
                <TextField
                    {...props}
                    {...styles}
                    type="qty"
                    {...{ formId: props.formId }}
                    disabled={isDisabled}
                    aria-label="Enter quantity"
                    showError={false}
                    onErrorHandler={errorHandler}
                />
                {showActions && (
                    <Clickable
                        {...styles.actionClickable}
                        type="button"
                        onClick={onIncreaseClickHandler}
                        aria-label="Increase quantity"
                        disabled={isDisabled || !canIncrease}
                    >
                        <Icon {...styles.countIcon} src={Plus} />
                    </Clickable>
                )}
            </StyledWrapper>
            {errorMessage && <Typography {...styles.errorMessage}>{errorMessage}</Typography>}
        </StyledWrapper>
    );
};

export default QtyField;
